import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import PoweredBy from '../components/common/PoweredBy';
import { ROUTES } from '../constants';
import './LandingPage.css';

const LandingPage = () => {
    const navigate = useNavigate();

    const scrollToAbout = () => {
        const element = document.getElementById('about');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="landing-page">
            <Header />

            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-container">
                    <div className="hero-content">
                        <h1 className="hero-title">
                            Welcome to <span className="gradient-text">AzimuthLab</span>
                        </h1>
                        <p className="hero-subtitle">
                            Modern inventory management solution designed to streamline your business operations,
                            track assets efficiently, and boost productivity.
                        </p>
                        <div className="hero-actions">
                            <button
                                className="btn-hero-primary"
                                onClick={() => navigate(ROUTES.DOWNLOAD)}
                            >
                                Get Started
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </button>
                            <button
                                className="btn-hero-secondary"
                                onClick={scrollToAbout}
                            >
                                Learn More
                            </button>
                        </div>
                    </div>
                    <div className="hero-visual">
                        <div className="hero-card card-1">
                            <div className="card-icon">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                                    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                                    <line x1="12" y1="22.08" x2="12" y2="12" />
                                </svg>
                            </div>
                            <h3>Inventory Tracking</h3>
                            <p>Real-time monitoring</p>
                        </div>
                        <div className="hero-card card-2">
                            <div className="card-icon">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 3h18v18H3zM21 9H3M21 15H3M12 3v18" />
                                </svg>
                            </div>
                            <h3>Purchase Orders</h3>
                            <p>Streamlined workflow</p>
                        </div>
                        <div className="hero-card card-3">
                            <div className="card-icon">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                                </svg>
                            </div>
                            <h3>Analytics</h3>
                            <p>Data-driven insights</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section id="about" className="about-section">
                <div className="about-container">
                    <div className="section-header">
                        <h2 className="section-title">About AzimuthLab</h2>
                        <p className="section-subtitle">
                            Empowering businesses with intelligent inventory management
                        </p>
                    </div>

                    <div className="about-content">
                        <div className="about-text">
                            <p className="about-description">
                                AzimuthLab is a comprehensive inventory management system designed for modern businesses.
                                We provide powerful tools to help you track inventory, manage purchase orders, coordinate
                                with suppliers, and gain valuable insights into your operations.
                            </p>
                            <p className="about-description">
                                Built with cutting-edge technology and a focus on user experience, AzimuthLab makes
                                complex inventory management simple and efficient.
                            </p>
                        </div>

                        <div className="features-grid">
                            <div className="feature-card">
                                <div className="feature-icon">
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                        <polyline points="22 4 12 14.01 9 11.01" />
                                    </svg>
                                </div>
                                <h3 className="feature-title">Real-Time Tracking</h3>
                                <p className="feature-description">
                                    Monitor your inventory levels in real-time with instant updates and notifications.
                                </p>
                            </div>

                            <div className="feature-card">
                                <div className="feature-icon">
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                                    </svg>
                                </div>
                                <h3 className="feature-title">Automated Workflows</h3>
                                <p className="feature-description">
                                    Streamline purchase orders and approvals with automated workflow management.
                                </p>
                            </div>

                            <div className="feature-card">
                                <div className="feature-icon">
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                        <circle cx="9" cy="7" r="4" />
                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                    </svg>
                                </div>
                                <h3 className="feature-title">Team Collaboration</h3>
                                <p className="feature-description">
                                    Enable seamless collaboration with role-based access and permissions.
                                </p>
                            </div>

                            <div className="feature-card">
                                <div className="feature-icon">
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="12" y1="1" x2="12" y2="23" />
                                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                                    </svg>
                                </div>
                                <h3 className="feature-title">Cost Management</h3>
                                <p className="feature-description">
                                    Track expenses, manage budgets, and optimize your inventory costs effectively.
                                </p>
                            </div>

                            <div className="feature-card">
                                <div className="feature-icon">
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                                    </svg>
                                </div>
                                <h3 className="feature-title">Advanced Analytics</h3>
                                <p className="feature-description">
                                    Gain insights with comprehensive reports and data visualization tools.
                                </p>
                            </div>

                            <div className="feature-card">
                                <div className="feature-icon">
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                </div>
                                <h3 className="feature-title">Secure & Reliable</h3>
                                <p className="feature-description">
                                    Enterprise-grade security with data encryption and regular backups.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>



            <Footer />
            <PoweredBy />
        </div>
    );
};

export default LandingPage;
